import { ApiCart, VLPVariant } from '@storefronts/common-types'
import type { LandingPageModel } from '../../models/landingPageModel'
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { v4 as uuidv4 } from 'uuid'
import { getStorefrontCart, upsertCart } from '../../api'
import { EL_USER_DATA } from '../analytics/elevar'
import { trackCartQuantityDeltaIfNeeded } from '../analytics/cartAnalytics'
import { AttentiveTracker } from '../components/pixels/AttentivePixel'
import { BlotoutTrackker } from '../components/pixels/BlotoutPixel'
import { ConvergeTracker } from '../components/pixels/ConvergePixel'
import { PostscriptTracker } from '../components/pixels/PostscriptPixel'
import { TripleWhaleTracker } from '../components/pixels/TripleWhalePixel'
import {
    getCartFromStorage,
    getFromStorageParsed,
    setCartInStorage,
    setInStorage,
} from '../storage'
import {
    AjaxShopifyCartItem,
    Cart,
    CartItem,
    isAjaxShopifyCartData,
    isApiCart,
    VariantWithImage,
} from '../types'
import { urlWithQueryParams } from '../utils/helpers'
import { applyShopPayToCheckoutUrl } from '../utils/checkoutPayment'
import { AnalyticsContext } from './analyticsContext'
import { CartContext } from './cartContext'
import { LandingPageLinksContext } from './LandingPageLinksContext'
import { ViddyLPContext } from './viddyLPContext'
import { UserEventDataContext } from './UserEventDataContextProvider'
import {
    IS_EDITOR_PREVIEW,
    QueryParamContext,
} from './QueryParamContextProvider'
import {
    convertAjaxToVariant,
    findVariantWithImageForAjaxCartItem,
} from '../utils/userEventTools'
import {
    getDiscountedCartTotal,
    getEffectiveFixedDiscountForCart,
    getUnDiscountedCartTotal,
    convertApiCartToCart,
    getCheckoutUrl,
    findCartItem,
} from '../utils/cartTools'

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [cart, setCart] = useState<Cart>({
        items: [],
        id: uuidv4(),
        isLoading: true,
    })
    const { landingPage, viddyId, discountCode } = useContext(ViddyLPContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { abandonmentStorefrontData, initialized } =
        useContext(UserEventDataContext)
    const { EVENT_ID, P_EVENT_ID, SHOP } = useContext(QueryParamContext)
    const CART_RECOVERED_KEY = useMemo(
        () => `__vd_cart_rec__${landingPage.landingPageHash}`,
        [landingPage.landingPageHash],
    )
    const [cartRecovered, setCartRecovered] = useState<boolean>(true)

    useEffect(() => {
        setCartRecovered(getFromStorageParsed(CART_RECOVERED_KEY) ?? false)
    }, [CART_RECOVERED_KEY])

    const { resolvedLandingPageLinks } = useContext(LandingPageLinksContext)
    const resolvedLandingPageLinksRef = useRef(resolvedLandingPageLinks)
    resolvedLandingPageLinksRef.current = resolvedLandingPageLinks

    // TODO: Move to a common place for CartProviders?
    const findVariantWithImageForAjaxCartItemCallback = useCallback(
        (item: AjaxShopifyCartItem): VariantWithImage | undefined => {
            return findVariantWithImageForAjaxCartItem(
                item,
                resolvedLandingPageLinks,
                landingPage.discount,
            )
        },
        [resolvedLandingPageLinks, landingPage.discount],
    )

    const convertAjaxToVariantCallback = useCallback(
        (item: AjaxShopifyCartItem): VariantWithImage => {
            return convertAjaxToVariant(item, landingPage.discount)
        },
        [landingPage.discount],
    )

    const pendingShopPayCheckoutRef = useRef(false)
    // The cart is initialised once; this keeps the effect from depending on the
    // cart state it writes to
    const cartInitStartedRef = useRef(false)

    const convertApiCartToCartCallback = useCallback(
        (recoveredCart: ApiCart) => {
            return convertApiCartToCart(
                recoveredCart,
                resolvedLandingPageLinksRef.current,
                landingPage.discount,
            )
        },
        [landingPage.discount],
    )

    useEffect(() => {
        if (IS_EDITOR_PREVIEW()) {
            // Editor preview: skip server-side cart init, cart UI works client-side only
            setCart((prevCart) => ({ ...prevCart, isLoading: false }))
            return
        }

        if (
            viddyId &&
            landingPage.landingPageHash &&
            !cartInitStartedRef.current
        ) {
            cartInitStartedRef.current = true
            // For now, let's use the locally stored cart if it exists as
            // it's possible for the cart to not be updated on the server if
            // the user quickly refreshes or exits the page before the upsertCart
            // is called.
            // The bonus is that the cart cookies will be updated with a shorter
            // expiration time.
            // Alternatively, we could get rid of this and just use the cart from the
            // server if we decide that we don't need to catch these cases.
            const cartFromStorage = getCartFromStorage(landingPage)
            if (cartFromStorage) {
                if (!cartFromStorage.id) {
                    cartFromStorage.id = uuidv4()
                }
                EL_USER_DATA(cartFromStorage)
                // If we have a cart in storage, let's make sure to call upsertCart
                // just in case the cart was not updated on the server and exit.
                // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
                _updateCartInStorage(
                    { ...cartFromStorage, isLoading: false },
                    false,
                )
                return
            }

            // No cart in local storage, let's fetch the cart from the server.
            const fetchCart = async (
                viddyId: string,
                landingPage: LandingPageModel,
            ) => {
                try {
                    const resp = await getStorefrontCart(
                        viddyId,
                        landingPage.landingPageHash,
                    )
                    if (!resp.isError) {
                        setCart((prevCart) => {
                            const newCart = resp.result
                                ? convertApiCartToCartCallback(resp.result)
                                : prevCart
                            newCart.isLoading = false
                            const updatedCart = { ...prevCart, ...newCart }
                            setCartInStorage(updatedCart, landingPage)
                            EL_USER_DATA(updatedCart)
                            return updatedCart
                        })
                        return
                    }
                    console.error(resp.error)
                    setCart((prevCart) => {
                        const newCart = { ...prevCart, isLoading: false }
                        return newCart
                    })
                } catch (e) {
                    console.error(e)
                    setCart((prevCart) => {
                        const newCart = { ...prevCart, isLoading: false }
                        return newCart
                    })
                }
            }

            fetchCart(viddyId, landingPage)
        }
    }, [landingPage, viddyId, convertApiCartToCartCallback])

    useEffect(() => {
        if (
            abandonmentStorefrontData?.recover_cart_data &&
            initialized &&
            // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
            !cartRecovered &&
            // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
            !cart.isLoading
        ) {
            // for now add items to cart by building cart items from supplied cart data.
            const recoveredCart = abandonmentStorefrontData.recover_cart_data
            let cartItems: CartItem[] = []
            if (isApiCart(recoveredCart)) {
                const cart = convertApiCartToCartCallback(recoveredCart)
                cartItems = cart.items
            } else if (isAjaxShopifyCartData(recoveredCart)) {
                cartItems = recoveredCart.items.map((item) => ({
                    variant:
                        findVariantWithImageForAjaxCartItemCallback(item) ??
                        convertAjaxToVariantCallback(item),
                    quantity: item.quantity,
                    // Do we add this even if CartProvider doesn't support subscriptions?
                    sellingPlanId: item.selling_plan_allocation?.selling_plan.id
                        ? `gid://shopify/SellingPlan/${item.selling_plan_allocation?.selling_plan.id}`
                        : undefined,
                    // item.properties contains the attributes in a json object.
                    attributes: item.properties,
                }))
            }
            if (cartItems.length > 0) {
                // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
                addCartItems(cartItems, false)
                    .then(() => {
                        setCartRecovered(true)
                        setInStorage(CART_RECOVERED_KEY, true)
                    })
                    .catch((error) => {
                        console.error(error)
                    })
            } else {
                setInStorage(CART_RECOVERED_KEY, true)
            }
        }
    }, [
        abandonmentStorefrontData?.recover_cart_data,
        initialized,
        cartRecovered,
        findVariantWithImageForAjaxCartItemCallback,
        convertAjaxToVariantCallback,
        convertApiCartToCartCallback,
        CART_RECOVERED_KEY,
        cart.isLoading,
    ])

    const _updateCartInStorage = async (
        newCart: Cart,
        immediateCheckout: boolean,
        shopPay?: boolean,
    ): Promise<Cart> => {
        if (!newCart.id) {
            const cartFromStorage = getCartFromStorage(landingPage)
            if (cartFromStorage) {
                newCart.id = cartFromStorage.id
            } else {
                newCart.id = uuidv4()
            }
        }
        const updatedCart = { ...newCart }
        setCartInStorage(updatedCart, landingPage)

        const discountCodes = discountCode ? [discountCode] : undefined
        setCart(updatedCart)

        const eventParams =
            EVENT_ID || P_EVENT_ID
                ? {
                      shop: SHOP ?? landingPage.client.shopifyShop,
                      eventId: EVENT_ID,
                      pEventId: P_EVENT_ID,
                  }
                : undefined

        try {
            const resp = await upsertCart(
                newCart,
                landingPage.client,
                viddyId,
                landingPage.landingPageHash,
                discountCodes,
                eventParams,
            )
            if (!resp.isError) {
                setCart((prevCart: Cart) => {
                    const updatedCart = {
                        ...prevCart,
                        id: resp.result.id ?? prevCart.id,
                        checkoutUrl: resp.result.checkoutUrl,
                        attributes:
                            resp.result.attributes ?? prevCart.attributes,
                    }
                    setCartInStorage(updatedCart, landingPage)
                    return updatedCart
                })
            }
        } catch (e) {
            // TODO: Handle errors
            console.log(e)
        }

        if (immediateCheckout) {
            pendingShopPayCheckoutRef.current = shopPay ?? false
            _onCheckout(updatedCart)
        }
        return updatedCart
    }

    /**
     * A variant can be in the cart twice — once as a one-time purchase and once
     * with a selling plan — so lookups take the plan into account: pass a
     * string/null to match that exact plan (null = no plan), or omit to match
     * any line with the variant.
     */
    const getItem = (
        variant: VLPVariant,
        sellingPlanId?: string | null,
        shopifyLineId?: string,
    ): CartItem | undefined =>
        findCartItem(cart.items, variant, {
            sellingPlanId,
            shopifyLineId,
        })

    const addItemsWithBuyXGetY = (
        variant: VariantWithImage,
        newItems: CartItem[],
    ): CartItem[] => {
        if (landingPage.discountCode !== 'STAYCOOL') return newItems
        newItems = [...newItems]
        const pillowCaseLink = resolvedLandingPageLinks.find(
            (link) => link.shopifyId === '8181188428011',
        )
        if (!pillowCaseLink) return newItems

        // Dosaze
        // If add contour pillow
        if (variant.link.shopifyId === '4516914987146') {
            const pillowCaseVariantIdx = variant.option1 == 'Queen' ? 0 : 1
            const pillowCaseIndex = newItems.findIndex(
                (item) => item.variant.shopifyId === '8181188428011',
            )
            if (pillowCaseIndex === -1) {
                const pillowCaseVariant =
                    pillowCaseLink.variantInfo &&
                    pillowCaseLink.variantInfo[pillowCaseVariantIdx]
                if (!pillowCaseVariant) return newItems
                const pillowCaseSelectedVariant = {
                    ...pillowCaseVariant,
                    imageUrl:
                        pillowCaseVariant.featuredImage ||
                        pillowCaseLink.imageUrl,
                    link: pillowCaseLink,
                    finalPrice: 0,
                    isSub: false,
                }
                pillowCaseSelectedVariant.price.compareAtPrice = 5000
                // pillowCaseVariant.shopifyId = '44350232494315'
                newItems = [
                    ...newItems,
                    { variant: pillowCaseSelectedVariant, quantity: 1 },
                ]
            }
        }
        return newItems
    }

    const removeItemsWithBuyXGetY = (
        variant: VariantWithImage,
        newItems: CartItem[],
    ): CartItem[] => {
        if (landingPage.discountCode !== 'STAYCOOL') return newItems
        newItems = [...newItems]
        const pillowCaseLink = resolvedLandingPageLinks.find(
            (link) => link.shopifyId === '8181188428011',
        )
        if (!pillowCaseLink) return newItems

        // Dosaze
        // If No Contour Pillow remains in cart, remove Pillow Case from cart
        if (variant.link.shopifyId == '4516914987146') {
            const pillowCaseIndex = newItems.findIndex(
                (item) => item.variant.link.shopifyId === '8181188428011',
            )
            if (pillowCaseIndex != -1) {
                newItems.splice(pillowCaseIndex, 1)
            }
        }

        return newItems
    }

    const addCartItem = async (
        variant: VariantWithImage,
        quantity: number = 1,
        sellingPlanId?: string | null,
        immediateCheckout: boolean = false,
        shopPay?: boolean,
    ) => {
        let newItems = [...cart.items]
        // Only merge with a line on the same selling plan; adding the same
        // variant with a different plan creates a separate cart line.
        const existingItem = getItem(variant, sellingPlanId ?? null)

        if (existingItem) {
            existingItem.quantity += quantity
            Analytics.TRACK_CART_ADD(variant, quantity)

            // Dosaze Buy X Get Y
            newItems = addItemsWithBuyXGetY(variant, newItems)

            const newCart = { ...cart, items: newItems }
            _updateCartInStorage(newCart, immediateCheckout, shopPay)
        } else {
            newItems.unshift({ variant, quantity, sellingPlanId })

            // Dosaze Buy X Get Y
            newItems = addItemsWithBuyXGetY(variant, newItems)

            const newCart = { ...cart, items: newItems }
            const updatedCart = await _updateCartInStorage(
                newCart,
                immediateCheckout,
                shopPay,
            )
            Analytics.TRACK_CART_ADD(variant, 1)
            AttentiveTracker.trackAddToCart(variant, cart.id)
            PostscriptTracker.trackAddToCart(variant, cart.id)
            TripleWhaleTracker.trackAddToCart(variant, cart.id)
            ConvergeTracker.trackAddToCart(variant, cart.id)
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            updatedCart && BlotoutTrackker.trackAddToCart(updatedCart)
        }
    }

    const addCartItems = async (
        variants: {
            variant: VariantWithImage
            quantity?: number
            sellingPlanId?: string | null
            attributes?: { [key: string]: string | null } | undefined
        }[],
        immediateCheckout: boolean = false,
        shopPay?: boolean,
    ) => {
        const existingItems = [...cart.items]
        const itemsToAdd: CartItem[] = []
        variants.forEach(
            ({ variant, quantity = 1, sellingPlanId, attributes }) => {
                const existingItem = getItem(variant, sellingPlanId ?? null)
                if (existingItem) {
                    existingItem.quantity += quantity
                } else {
                    itemsToAdd.push({
                        variant,
                        quantity: quantity ?? 1,
                        sellingPlanId,
                        attributes,
                    })
                }
                Analytics.TRACK_CART_ADD(variant, quantity)
            },
        )

        const newCart = { ...cart, items: [...itemsToAdd, ...existingItems] }
        const updatedCart = await _updateCartInStorage(
            newCart,
            immediateCheckout,
            shopPay,
        )
        variants.forEach(({ variant }) => {
            AttentiveTracker.trackAddToCart(variant, cart.id)
            PostscriptTracker.trackAddToCart(variant, cart.id)
            TripleWhaleTracker.trackAddToCart(variant, cart.id)
            ConvergeTracker.trackAddToCart(variant, cart.id)
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            updatedCart && BlotoutTrackker.trackAddToCart(updatedCart)
        })
    }

    const updateCartItem = (
        variant: VariantWithImage,
        quantity?: number,
        sellingPlanId?: string | null,
        immediateCheckout: boolean = false,
        shopPay?: boolean,
        currentSellingPlanId?: string | null,
        shopifyLineId?: string,
    ) => {
        const existingItem = getItem(
            variant,
            currentSellingPlanId,
            shopifyLineId,
        )

        if (existingItem) {
            const previousQuantity = existingItem.quantity
            const newQuantity = quantity ?? existingItem.quantity
            const newItems = [...cart.items]
            existingItem.quantity = newQuantity
            // undefined means "leave the plan as is" (e.g. quantity changes).
            if (sellingPlanId !== undefined) {
                // Switching plans: if another line already has the target
                // plan, fold this line into it instead of leaving two lines
                // with the same variant and plan.
                const targetItem = getItem(variant, sellingPlanId ?? null)
                if (targetItem && targetItem !== existingItem) {
                    targetItem.quantity += existingItem.quantity
                    newItems.splice(newItems.indexOf(existingItem), 1)
                } else {
                    existingItem.sellingPlanId = sellingPlanId
                }
            }
            trackCartQuantityDeltaIfNeeded(
                Analytics,
                variant,
                previousQuantity,
                newQuantity,
                sellingPlanId,
            )
            const newCart = { ...cart, items: newItems }
            _updateCartInStorage(newCart, immediateCheckout, shopPay)
        } else {
            addCartItem(
                variant,
                quantity,
                sellingPlanId,
                immediateCheckout,
                shopPay,
            )
        }
    }

    const removeCartItem = (
        variant: VariantWithImage,
        sellingPlanId?: string | null,
        shopifyLineId?: string,
    ) => {
        let newItems = [...cart.items]
        const existingItem = getItem(variant, sellingPlanId, shopifyLineId)

        if (existingItem) {
            const idx = cart.items.indexOf(existingItem)
            newItems.splice(idx, 1)
        }

        // Dosaze Buy X Get Y
        newItems = removeItemsWithBuyXGetY(variant, newItems)

        if (existingItem) {
            Analytics.TRACK_CART_REMOVE_ITEM(variant, existingItem.quantity)
        }

        _updateCartInStorage({ ...cart, items: newItems }, false)
    }

    const _onCheckout = (anyCart: Cart) => {
        const useShopPay = pendingShopPayCheckoutRef.current
        pendingShopPayCheckoutRef.current = false

        const _checkout = (aCart: Cart) => {
            let domain: null | string = null
            if (landingPage.client.mainDomain) {
                domain = landingPage.client.mainDomain
            } else if (landingPage.client.shopifyShop) {
                domain = landingPage.client.shopifyShop
            }
            if (!domain) {
                console.error('No domain found for checkout')
                return
            }

            const url = getCheckoutUrl(
                landingPage.client,
                aCart,
                discountCode ?? undefined,
                'CartProvider:',
            )

            if (
                url.includes('.myshopify.com') &&
                // the selling plan permallink should be on myshopify.com
                !url.includes('cart/clear')
            ) {
                const sanitized = urlWithQueryParams(
                    `https://${domain}${url.split('.myshopify.com')[1]}`,
                )
                const withShopPay = applyShopPayToCheckoutUrl(
                    sanitized,
                    useShopPay,
                )
                const href =
                    ConvergeTracker.appendCrossDomainParams(withShopPay)
                console.log(`CartProvider: Checkout URL: ${href}`)
                window.location.href = href
            } else {
                const fixedUrl = urlWithQueryParams(url)
                const withShopPay = applyShopPayToCheckoutUrl(
                    fixedUrl,
                    useShopPay,
                )
                const href =
                    ConvergeTracker.appendCrossDomainParams(withShopPay)
                console.log(`CartProvider: Checkout URL: ${href}`)
                window.location.href = href
            }
        }

        const discountCodes = discountCode ? [discountCode] : undefined

        if (anyCart.checkoutUrl) {
            _checkout(anyCart)
            return
        }
        upsertCart(
            anyCart,
            landingPage.client,
            viddyId,
            landingPage.landingPageHash,
            discountCodes,
        )
            .then((resp) => {
                if (!resp.isError) {
                    setCart((prevCart) => {
                        const newCart = {
                            ...prevCart,
                            checkoutUrl:
                                resp.result.checkoutUrl ?? prevCart.checkoutUrl,
                            attributes:
                                resp.result.attributes ?? prevCart.attributes,
                        }
                        _checkout(newCart)
                        return newCart
                    })
                    return
                }
                _checkout(anyCart)
            })
            .catch((e) => {
                console.log(e)
                _checkout(anyCart)
            })
    }

    const onCheckout = useCallback(
        (shopPay?: boolean) => {
            pendingShopPayCheckoutRef.current = shopPay ?? false
            _onCheckout(cart)
        },
        [landingPage, cart],
    )

    const getCartTotal = useCallback(() => {
        const fixedDiscount = getEffectiveFixedDiscountForCart(
            cart,
            landingPage.discount ?? null,
        )
        return getDiscountedCartTotal(cart, false, fixedDiscount)
    }, [cart, landingPage.discount])

    const getCartSubtotal = useCallback(() => {
        return getUnDiscountedCartTotal(cart, false, {
            landingPageLinks: resolvedLandingPageLinksRef.current,
            discount: landingPage.discount,
        })
    }, [cart, landingPage.discount])

    return (
        <CartContext.Provider
            value={{
                cart,
                updateCartItem,
                removeCartItem,
                addCartItem,
                onCheckout,
                addCartItems,
                getCartSubtotal,
                getCartTotal,
            }}
        >
            {children}
        </CartContext.Provider>
    )
}
